import React, { useEffect, useState } from 'react';
import {
    Box,
    Checkbox,
    List,
    ListItem,
    Stack,
    Typography,
    Select,
    MenuItem,
    FormControl,
    useMediaQuery,
    useTheme,
    Divider,
} from '@mui/material';
import upilogo from '../../../../assets/upi-logo.svg';
import { useDispatch, useSelector } from 'react-redux';
import { handleAmount } from '../../../../shared/extra';
import { calculatePayment } from '../customPaymentHook';
import { QrCode } from 'lucide-react';
import { setProductDetails } from '../../../../store/productInfo/action';
import CouponDrawer from './CouponDrawer';
import netbanking from '../../../../assets/netBanking.png';
import debit from '../../../../assets/debitcard.png';
import credit from '../../../../assets/creditcard.png';

function PayFromWallet({ walletPayment, setWalletPayment }) {
    const [netPay, setNetPay] = useState(0);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const dispatch = useDispatch();

    const userProfile = useSelector((state) => state.UserProfile.allData?.data);
    const PRODUCTDETAILS = useSelector((state) => state.ProductDetail?.allData);
    const DENOMINATION_STORE_DATA = useSelector((state) => state.Denominations?.allData);
    const balance = userProfile?.balance || 0;

    const paymentMethods = Array.isArray(PRODUCTDETAILS?.payment_methods)
        ? PRODUCTDETAILS.payment_methods
        : [];

    /* ---------- DERIVED FLAGS ---------- */
    const isWalletSelected = walletPayment?.isWalletPayment;
    const isWalletEnough = balance >= netPay && netPay > 0;
    const isShowPaymentOptions = isWalletSelected && isWalletEnough && !PRODUCTDETAILS?.selectedPaymentMethod?.id;

    /* ---------- NET PAY ---------- */
    useEffect(() => {
        if (!DENOMINATION_STORE_DATA?.length || !PRODUCTDETAILS) {
            setNetPay(0);
            return;
        }

        const res = calculatePayment(
            DENOMINATION_STORE_DATA,
            PRODUCTDETAILS?.discount,
            PRODUCTDETAILS?.isCashbackProduct
        );

        setNetPay(res?.netPaymentAmt);
    }, [DENOMINATION_STORE_DATA, PRODUCTDETAILS]);

    const grossAmount = DENOMINATION_STORE_DATA?.length
        ? calculatePayment(
            DENOMINATION_STORE_DATA,
            PRODUCTDETAILS?.discount,
            PRODUCTDETAILS?.isCashbackProduct
        )?.totalGrossAmt || 0
        : 0;
    /* ---------- AUTO SELECTION LOGIC ---------- */
    useEffect(() => {
        if (!PRODUCTDETAILS || !paymentMethods?.length) return;

        const updated = { ...PRODUCTDETAILS };

        const upiMethod = paymentMethods?.find((m) => m.name?.toLowerCase() === 'upi' && m.isActive);

        if (isWalletSelected && isWalletEnough) {
            updated.selectedPaymentMethod = null;
            updated.discount = updated.originalDiscount;
            dispatch(setProductDetails(updated));
            return;
        }

        // ✅ Sirf active method auto select
        if (upiMethod && !updated.selectedPaymentMethod) {
            updated.selectedPaymentMethod = upiMethod;
            updated.discount = updated.originalDiscount - upiMethod.percentage;
            dispatch(setProductDetails(updated));
        }
    }, [isWalletSelected, isWalletEnough, paymentMethods]);

    /* ---------- WALLET TOGGLE ---------- */
    const handleWalletSelection = () => {
        if (balance <= 0 || netPay <= 0) return;

        setWalletPayment((prev) => ({
            isWalletPayment: !prev?.isWalletPayment,
        }));
    };

    /* ---------- PAYMENT METHOD SELECTION ---------- */
    const paymentMethodSelection = (method) => {
        if (!method || !method.isActive) return;

        if (isWalletSelected && isWalletEnough) return;

        const updated = { ...PRODUCTDETAILS };

        if (method.id !== PRODUCTDETAILS?.selectedPaymentMethod?.id) {
            updated.selectedPaymentMethod = method;
            updated.discount = updated.originalDiscount - method.percentage;
        }

        dispatch(setProductDetails(updated));
    };

    /* ---------- DYNAMIC ICONS ---------- */

    const getPaymentIcon = (methodName) => {
        const name = methodName?.toLowerCase() || '';

        if (name.includes('upi')) return <Box component="img" src={upilogo} alt="UPI" sx={{ width: 40, height: 'auto' }} />;
        if (name.includes('credit'))
            return <Box component="img" src={credit} alt="Credit Card" style={{ width: 40, height: 'auto' }} />;
        if (name.includes('debit'))
            return <Box component="img" src={debit} alt="Debit Card" style={{ width: 40, height: 'auto' }} />;
        if (name.includes('net'))
            return <Box component="img" src={netbanking} alt="Net Banking" style={{ width: 40, height: 'auto' }} />;

        return <QrCode size={20} stroke="#000" />;
    };

    /* ---------- MOBILE DROPDOWN ---------- */
    const MobilePaymentDropdown = () => (
        <FormControl fullWidth size="small" sx={{ mt: 1 }}>
            <Select
                value={PRODUCTDETAILS?.selectedPaymentMethod?.id ?? ''}
                displayEmpty
                disabled={isWalletSelected && isWalletEnough}
                sx={{ borderRadius: 2, border: '1px solid #ccc', height: 52 }}
                onChange={(e) => {
                    const selected = paymentMethods?.find((m) => String(m.id) === String(e.target.value));
                    paymentMethodSelection(selected);
                }}
                renderValue={(selectedId) => {
                    if (!selectedId) return
                    <Typography color="text.disabled" fontSize={14}>
                        Select Payment Method
                    </Typography>;
                    const method = paymentMethods?.find((m) => String(m.id) === String(selectedId));
                    if (!method || !method.isActive) {
                        return <Typography color="text.disabled" fontSize={14}>
                            Select Payment Method
                        </Typography>
                    }
                    return (
                        <Stack direction="row" alignItems="center" spacing={1}>
                            {getPaymentIcon(method.name)}
                            <Divider orientation='vertical' sx={{ height: 30, borderColor: 'gray', borderWidth: 0.2 }} />
                            <Stack direction="column" spacing={0.2}>
                                <Typography fontSize={13} textTransform="capitalize">{method?.name}</Typography>
                                <Typography fontSize={12} color="text.secondary" textTransform="capitalize">
                                    charge ({method.percentage}%)
                                </Typography>
                            </Stack>
                        </Stack>
                    );
                }}
            >
                {paymentMethods?.map((method, idx) => (
                    <React.Fragment key={method.id}>
                        <MenuItem dense value={method.id} disabled={!method.isActive}>
                            <Stack direction="row" alignItems="center" spacing={1}>
                                {getPaymentIcon(method.name)}
                                <Stack direction="column" spacing={0.2}>
                                    <Typography fontSize={14} sx={{ textTransform: 'capitalize' }}>{method.name}</Typography>
                                    <Typography fontSize={12} color="text.secondary">
                                        charge ({method.percentage}%)
                                    </Typography>
                                </Stack>
                            </Stack>
                        </MenuItem>

                        {idx !== paymentMethods.length - 1 && (
                            <Divider />
                        )}
                    </React.Fragment>
                ))}
            </Select>
        </FormControl>
    );

    /* ---------- DESKTOP CARDS ---------- */
    const DesktopPaymentMethods = () => (
        <Box
            sx={{
                display: 'grid',
                gap: 2,
                gridTemplateColumns: {
                    md: 'repeat(2, 1fr)',
                    lg: 'repeat(2, 1fr)',
                },
            }}
        >
            {paymentMethods?.map((method) => {
                const isSelected = method.id === PRODUCTDETAILS?.selectedPaymentMethod?.id;

                // const disabled = isWalletSelected && isWalletEnough;
                const disabled = !method.isActive || (isWalletSelected && isWalletEnough);

                return (
                    <Stack
                        key={method.id}
                        direction="row"
                        alignItems="center"
                        spacing={{ sm: 1, md: 1.2, lg: 1 }}
                        onClick={() => !disabled && paymentMethodSelection(method)}
                        sx={[
                            {
                                backgroundColor: '#fff',
                                borderRadius: 2,
                                cursor: 'pointer',
                                opacity: 1,
                                pointerEvents: 'auto',
                                userSelect: 'none',

                                height: { sm: 52, md: 56, lg: 60 },
                                px: { sm: 1.2, md: 1.5, lg: 2 },
                                py: { sm: 0.8, md: 1, lg: 1.5 },
                            },
                            {
                                '&:hover': {
                                    backgroundColor: '#f5f5f5',
                                },
                            },
                            disabled && {
                                cursor: 'wait',
                                opacity: 0.5,
                                pointerEvents: 'none',
                            },
                        ]}
                    >
                        {/* 🔘 Radio Circle */}
                        < Box
                            sx={{
                                width: { sm: 14, md: 16, lg: 18 },
                                height: { sm: 14, md: 16, lg: 18 },
                                borderRadius: '50%',
                                border: '1px solid',
                                borderColor: isSelected ? 'primary.main' : '#bdbdbd',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                            }}
                        >
                            {isSelected && (
                                <Box
                                    sx={{
                                        width: { sm: 6, md: 8, lg: 10 },
                                        height: { sm: 6, md: 8, lg: 10 },
                                        borderRadius: '50%',
                                        backgroundColor: 'primary.main',
                                    }}
                                />
                            )}
                        </Box>

                        {/* 🖼️ Icon */}
                        <Box
                            sx={{
                                width: { sm: 28, md: 32, lg: 45 },
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                            }}
                        >
                            {getPaymentIcon(method.name)}
                        </Box>

                        {/* 📝 Text */}
                        <Stack spacing={0.2}>
                            <Typography
                                fontWeight={500}
                                fontSize={{ sm: '0.8rem', md: '0.9rem', lg: '1.2rem' }}
                                lineHeight={1.2}
                                sx={{
                                    color: 'textPrimary.main',
                                    textTransform: 'capitalize',
                                }}
                            >
                                {method.name}
                            </Typography>

                            <Typography
                                color="text.secondary"
                                fontSize={{ sm: 10, md: 11, lg: 12 }}
                                lineHeight={1.2}
                            >
                                charge {method.percentage}%
                            </Typography>
                        </Stack>
                    </Stack>
                );
            })}
        </Box >
    );

    return (
        <Box>
            <CouponDrawer productId={PRODUCTDETAILS?.id} grossAmount={netPay} />

            <Box sx={{ mt: 2 }}>
                <Typography color={'primary.main'} fontWeight={500}>
                    Pay With Wallet
                </Typography>

                {/* ---------- WALLET ---------- */}
                <List>
                    <ListItem
                        sx={{
                            border: '1.5px solid',
                            borderColor: 'divider',
                            borderRadius: 2,
                            py: 0.5,
                            height: 44,
                            justifyContent: 'space-between',
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography fontWeight={400} sx={{ color: 'textPrimary.main' }}>
                                Available Balance:
                            </Typography>
                            <Typography fontWeight={500} sx={{ color: 'textPrimary.main' }}>
                                ₹{balance >= 1 ? handleAmount(balance) : '0.00'}
                            </Typography>
                        </Box>

                        <Checkbox
                            checked={isWalletSelected}
                            onClick={handleWalletSelection}
                            disabled={balance <= 0 || netPay <= 0}
                        />
                    </ListItem>
                </List>
            </Box>

            {paymentMethods?.length > 0 && (
                <Box sx={{ mt: 2, display: isMobile && isShowPaymentOptions ? 'none' : 'block' }}>
                    <Typography color={'primary.main'} fontWeight={500}>
                        Select Payment Methods
                    </Typography>

                    {/* ---------- PAYMENT METHODS ---------- */}
                    {paymentMethods?.length > 0 &&
                        (isMobile ? <MobilePaymentDropdown /> : <DesktopPaymentMethods />)}
                </Box>)}
        </Box>
    );
}

export default PayFromWallet;
